import React, {useState, useRef, useEffect} from 'react'
import {AnimatePresence, motion} from 'framer-motion'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Heading,
    Text,
    useDisclosure,
    useBreakpointValue,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    DrawerFooter,
    Center,
    Spinner,
    Flex,
    Portal
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {ChevronLeftIcon, ChevronDownIcon} from '../../../components/icons'
import {animationVariants} from './animation-variants'
import {useParams} from 'react-router-dom'
import {useSortUrls} from '../../../hooks/use-sort-urls'
import {useRefinements} from '../../../hooks/use-refinements'
import {withCommerceSdkReact} from '@salesforce/retail-react-app/app/components/with-commerce-sdk-react/with-commerce-sdk-react'
import {useCategory} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getRootCategoryId} from '../../../utils/brandUtils'
import {FormattedMessage} from 'react-intl'
import {useHeaderContext} from '../../../contexts/header-context'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import NestedCategoryMenu from './nested-categories'
import Refinements from './refinements'
import FiltersHeader from './filters-header'
import SelectedFiltersHeader from './selected-filters-header'
import {useStickyThreshold} from '../../../hooks/use-sticky-threshold'

const MotionBox = motion(Box)

const findCategoryPath = (node, targetId, path = []) => {
    if (!node) return null
    if (node.id === targetId) return [...path, node]
    for (const child of node.categories || []) {
        const result = findCategoryPath(child, targetId, [...path, node])
        if (result) return result
    }
    return null
}

const PlaceholderComponent = () => (
    <Center py={'20px'} px={'32px'} borderBottom={'1px solid'} borderColor={'gray.50'}>
        <Spinner size="md" />
    </Center>
)

const MobileSortSection = ({onSortClick, handleFilterClick, refinements}) => {
    const firstRefinementId =
        refinements && Object.keys(refinements).length > 0
            ? refinements[Object.keys(refinements)[0]]?.[0]?.attributeId
            : 'allFilters'

    return (
        <Flex justify="space-between" align="center" spacing={1} bg="white">
            <Button
                fontSize="sm"
                colorScheme="black"
                variant="filter"
                onClick={() => handleFilterClick(firstRefinementId)}
                display="inline-flex"
            >
                <FormattedMessage
                    id="product_list.button.all_filters"
                    defaultMessage="All Filters"
                />
            </Button>

            <Button
                rightIcon={<ChevronDownIcon boxSize={5} />}
                variant="sortSelect"
                onClick={onSortClick}
            >
                <FormattedMessage id="product_list.drawer.title.sort_by" defaultMessage="Sort By" />
            </Button>
        </Flex>
    )
}

MobileSortSection.propTypes = {
    onSortClick: PropTypes.func.isRequired,
    handleFilterClick: PropTypes.func.isRequired,
    refinements: PropTypes.object
}

const PageFilters = ({
    category,
    productSearchResult,
    isLoading,
    resetFilters,
    onSortClick,
    isVisible = true
}) => {
    const {categoryId} = useParams()
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {site} = useMultiSite()

    const [navDirection, setNavDirection] = useState('forward')
    const [activeFilter, setActiveFilter] = useState(null)
    const [categoryStack, setCategoryStack] = useState([category])

    const filterRefs = useRef({})

    const currentCategory = categoryStack[categoryStack.length - 1]
    const rootCategory = getRootCategoryId(site.id, 'garage')
    const isRootCategory = currentCategory?.id === rootCategory
    const sizeGrouping = category?.c_sizeGrouping
    const showNoFilterMatch = !isLoading && productSearchResult && !productSearchResult?.hits

    const sortUrls = useSortUrls({
        options: productSearchResult?.sortingOptions
    })

    const {groupedRefinements, selectedFilters, toggleFilter} = useRefinements({
        refinements: productSearchResult?.refinements,
        sizeGrouping: sizeGrouping,
        category: category
    })

    const {shouldShowSticky, headerRef} = useHeaderContext()
    const {filtersRef} = useStickyThreshold()
    const isMobile = useBreakpointValue({base: true, lg: false})

    const isSmartSearch = productSearchResult?.isSmartSearch

    const handleFilterClick = (filterId) => {
        filterId = !(filterId?.indexOf('Size') > 0) ? filterId : 'c_size'
        setActiveFilter(filterId)

        if (filterId?.toLowerCase() === 'category') {
            const path =
                category && categoryId ? findCategoryPath(category, categoryId) : [category]
            const currentCat = path?.[path.length - 1]

            if (currentCat?.categories?.length) {
                setCategoryStack(path)
            } else if (currentCat?.parentCategoryId) {
                setCategoryStack([{id: currentCat.parentCategoryId}])
            } else {
                setCategoryStack(path || [category])
            }
        }

        onOpen()
    }

    const resetFiltersOnClose = () => {
        setActiveFilter(null)
        onClose()
    }

    const navigateBack = () => {
        setNavDirection('backward')
        if (categoryStack.length > 1) {
            setCategoryStack((prev) => prev.slice(0, -1))
        } else if (currentCategory?.parentCategoryId) {
            setCategoryStack([{id: currentCategory.parentCategoryId}])
        }
    }

    const SubCategoriesTree = withCommerceSdkReact(
        ({currentCategory, data, ...rest}) => {
            if (data?.id && data?.categories) {
                setCategoryStack([{...data, categories: data.categories}])
                setNavDirection('forward')
            }

            return (
                <NestedCategoryMenu
                    currentCategory={currentCategory}
                    navDirection={navDirection}
                    navigateBack={navigateBack}
                    onClose={onClose}
                    categoriesList={currentCategory?.categories || []}
                    CategoryTree={SubCategoriesTree}
                    withReactQuery={true}
                    {...rest}
                />
            )
        },
        {
            hook: useCategory,
            queryOptions: ({item}) => ({parameters: {id: item?.id || rootCategory}}),
            placeholder: PlaceholderComponent
        }
    )

    useEffect(() => {
        if (isOpen && activeFilter && filterRefs.current[activeFilter]) {
            setTimeout(() => {
                filterRefs.current[activeFilter]?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                })
            }, 100)
        }
    }, [isOpen, activeFilter])

    const renderDrawerContent = () => {
        if (isLoading) return <LoadingSpinner />

        if (activeFilter?.toLowerCase() === 'category') {
            return (
                <AnimatePresence mode="wait" initial={false} custom={navDirection}>
                    <MotionBox
                        key={currentCategory?.id || 'fallback'}
                        variants={animationVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        custom={navDirection}
                        transition={{duration: 0.1, ease: 'easeInOut'}}
                        position="relative"
                    >
                        {currentCategory?.name ? (
                            <NestedCategoryMenu
                                currentCategory={currentCategory}
                                navDirection={navDirection}
                                navigateBack={navigateBack}
                                onClose={onClose}
                                isRootCategory={isRootCategory}
                                categoriesList={currentCategory?.categories || []}
                                CategoryTree={SubCategoriesTree}
                                withReactQuery={false}
                            />
                        ) : (
                            <SubCategoriesTree item={currentCategory} />
                        )}
                    </MotionBox>
                </AnimatePresence>
            )
        }

        return (
            <Refinements
                isLoading={isLoading}
                toggleFilter={toggleFilter}
                filters={productSearchResult?.refinements}
                excludedFilters={['cgid']}
                selectedFilters={selectedFilters}
                initiallyExpandedFilters={[activeFilter]}
                sizeGrouping={sizeGrouping}
                filterRefs={filterRefs}
                productSearchResult={productSearchResult}
                category={category}
            />
        )
    }

    return (
        <>
            <Box ref={filtersRef} display={isVisible ? undefined : 'none'}>
                {isVisible && !shouldShowSticky && !isLoading && !isSmartSearch && (
                    <FiltersHeader
                        handleFilterClick={handleFilterClick}
                        refinements={groupedRefinements}
                        sortUrls={sortUrls}
                        productSearchResult={productSearchResult}
                    />
                )}
            </Box>

            {isVisible && shouldShowSticky && !isLoading && !isSmartSearch && (
                <Portal containerRef={headerRef}>
                    <Box maxWidth="container.xxxl" mx="auto" px={[4, 4, 6, 8]} py={[4, 4, 5, 5]}>
                        {isMobile ? (
                            <MobileSortSection
                                onSortClick={onSortClick}
                                handleFilterClick={handleFilterClick}
                                refinements={groupedRefinements}
                            />
                        ) : (
                            <FiltersHeader
                                handleFilterClick={handleFilterClick}
                                refinements={groupedRefinements}
                                sortUrls={sortUrls}
                                productSearchResult={productSearchResult}
                                containerProps={{mb: 0}}
                            />
                        )}
                    </Box>
                </Portal>
            )}

            <Drawer
                placement="right"
                isOpen={isOpen}
                blockScrollOnMount
                variant="filterDrawer"
                preserveScrollBarGap={false}
                size="sm"
                onClose={resetFiltersOnClose}
            >
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerHeader>
                        <Heading
                            as="p"
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={
                                activeFilter?.toLowerCase() === 'category'
                                    ? 'space-between'
                                    : 'start'
                            }
                            textAlign={'center'}
                            color={'black'}
                            fontWeight={'500'}
                            fontSize={'14px'}
                            textTransform={'uppercase'}
                        >
                            {activeFilter?.toLowerCase() === 'category' &&
                            !isRootCategory &&
                            currentCategory?.onlineSubCategoriesCount > 1 ? (
                                <Button
                                    mr={0}
                                    p={0}
                                    onClick={navigateBack}
                                    variant="ghost"
                                    size="sm"
                                >
                                    <ChevronLeftIcon color={'black'} />
                                </Button>
                            ) : (
                                <div />
                            )}

                            {isLoading && <LoadingSpinner />}
                            <Text isTruncated>
                                {activeFilter?.toLowerCase() === 'category' &&
                                    currentCategory?.name}
                            </Text>
                            {activeFilter?.toLowerCase() === 'category' && (
                                <DrawerCloseButton position={'static'} />
                            )}
                        </Heading>
                        {activeFilter?.toLowerCase() !== 'category' && (
                            <SelectedFiltersHeader
                                productSearchResult={productSearchResult}
                                toggleFilter={toggleFilter}
                                resetFilters={resetFilters}
                            />
                        )}
                    </DrawerHeader>
                    <DrawerBody>{renderDrawerContent()}</DrawerBody>
                    {activeFilter?.toLowerCase() !== 'category' && (
                        <DrawerFooter
                            pt={{base: 4, lg: 7}}
                            pb={{base: 6, lg: 7}}
                            px={{base: 4, lg: '32px !important'}}
                            borderTop="1px solid"
                            borderColor="gray.200"
                        >
                            <Button variant="solid" width="100%" onClick={onClose}>
                                {showNoFilterMatch ? (
                                    <FormattedMessage
                                        id="filters.no_match"
                                        defaultMessage="No Matches available"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="filters.see_results"
                                        defaultMessage="{count, plural, one {See # result} other {See # results}}"
                                        values={{count: productSearchResult?.total || 0}}
                                    />
                                )}
                            </Button>
                        </DrawerFooter>
                    )}
                </DrawerContent>
            </Drawer>
        </>
    )
}

PageFilters.propTypes = {
    isLoading: PropTypes.bool,
    category: PropTypes.object,
    productSearchResult: PropTypes.object,
    resetFilters: PropTypes.func,
    selectedSortingOptionLabel: PropTypes.object,
    selectedLabel: PropTypes.string,
    onSortClick: PropTypes.func,
    onFiltersClick: PropTypes.func,
    isVisible: PropTypes.bool
}

export default PageFilters
